import type { Device } from '@/devices/types';
import type { PipePortType } from '@/project/types';

export type PortDirection = 'up' | 'down' | 'left' | 'right';

export interface DevicePort {
  readonly id: string;
  readonly deviceId: string;
  readonly portType: PipePortType;
  readonly name: string;
  readonly relativePos: { x: number; y: number };
  readonly direction: PortDirection;
}

export interface WorldPort extends DevicePort {
  readonly worldPos: { x: number; y: number };
}

/**
/**
 * Calculates Y position for multiple tank inlets on the left side of the tank.
 */
export function getLeftInletY(index: number, count: number): number {
  if (count <= 1) return 55;
  if (count === 2) return index === 0 ? 60 : 96;
  if (count === 3) return 50 + index * 26; // 50, 76, 102
  if (count === 4) return 46 + index * 20; // 46, 66, 86, 106
  const spacing = 70 / (count - 1);
  return 46 + index * spacing;
}

/**
 * Returns available fluid pipe ports for a given device.
 */
export function getDevicePorts(device: Device): DevicePort[] {
  const type = device.type;
  const isPumpMotor = type === 'motor' && device.props['variant'] === 'pump_motor';

  if (type === 'tank') {
    const inletCount = Math.max(1, Math.min(4, Number(device.props['inletCount']) || 1));
    const ports: DevicePort[] = [];

    if (inletCount === 1) {
      ports.push({
        id: `${device.id}-inlet`,
        deviceId: device.id,
        portType: 'inlet',
        name: 'Boru Girişi (Üst)',
        relativePos: { x: 65, y: 4.5 },
        direction: 'up',
      });
    } else {
      for (let i = 0; i < inletCount; i++) {
        const y = getLeftInletY(i, inletCount);
        ports.push({
          id: `${device.id}-inlet_${i + 1}`,
          deviceId: device.id,
          portType: 'inlet',
          name: `Boru Girişi ${i + 1} (Sol)`,
          relativePos: { x: 4, y },
          direction: 'left',
        });
      }
    }

    ports.push({
      id: `${device.id}-outlet`,
      deviceId: device.id,
      portType: 'outlet',
      name: 'Boru Çıkışı (Alt)',
      relativePos: { x: 136, y: 148 },
      direction: 'right',
    });

    return ports;
  }

  if (type === 'pump' || isPumpMotor) {
    return [
      {
        id: `${device.id}-inlet`,
        deviceId: device.id,
        portType: 'inlet',
        name: 'Emiş Girişi (Sol)',
        relativePos: { x: 4, y: 56 },
        direction: 'left',
      },
      {
        id: `${device.id}-outlet`,
        deviceId: device.id,
        portType: 'outlet',
        name: 'Basma Çıkışı (Üst)',
        relativePos: { x: 42, y: 4 },
        direction: 'up',
      },
    ];
  }

  if (type === 'valve') {
    return [
      {
        id: `${device.id}-inlet`,
        deviceId: device.id,
        portType: 'inlet',
        name: 'Vana Girişi (Sol)',
        relativePos: { x: 0, y: 50 },
        direction: 'left',
      },
      {
        id: `${device.id}-outlet`,
        deviceId: device.id,
        portType: 'outlet',
        name: 'Vana Çıkışı (Sağ)',
        relativePos: { x: 100, y: 50 },
        direction: 'right',
      },
    ];
  }

  return [];
}

/**
 * Returns the world position and direction of a device port.
 */
export function getPortWorldPosition(
  device: Device,
  portType: PipePortType,
  portId?: string | undefined
): { x: number; y: number; dir: PortDirection } | null {
  const ports = getDevicePorts(device);
  let port: DevicePort | undefined;
  if (portId) {
    port = ports.find((p) => p.id === portId);
  }
  if (!port) {
    port = ports.find((p) => p.portType === portType);
  }
  if (!port) return null;

  const posX = device.transform?.position?.x ?? 0;
  const posY = device.transform?.position?.y ?? 0;

  return {
    x: posX + port.relativePos.x,
    y: posY + port.relativePos.y,
    dir: port.direction,
  };
}

/**
 * Returns all pipe ports in the entire scene with their world coordinates.
 */
export function getAllScenePorts(devices: readonly Device[]): WorldPort[] {
  const list: WorldPort[] = [];
  for (const d of devices) {
    const ports = getDevicePorts(d);
    const posX = d.transform?.position?.x ?? 0;
    const posY = d.transform?.position?.y ?? 0;
    for (const p of ports) {
      list.push({
        ...p,
        worldPos: {
          x: posX + p.relativePos.x,
          y: posY + p.relativePos.y,
        },
      });
    }
  }
  return list;
}

interface Point {
  x: number;
  y: number;
}

/**
 * Converts a sequence of orthogonal points into an SVG path string with rounded corners.
 */
export function roundOrthogonalCorners(points: Point[], radius = 10): string {
  if (points.length < 2) return '';
  const first = points[0]!;
  if (points.length === 2) {
    const second = points[1]!;
    return `M ${first.x} ${first.y} L ${second.x} ${second.y}`;
  }

  let d = `M ${first.x} ${first.y}`;

  for (let i = 1; i < points.length - 1; i++) {
    const pPrev = points[i - 1]!;
    const pCurr = points[i]!;
    const pNext = points[i + 1]!;

    // Distance to previous and next points
    const dPrev = Math.hypot(pCurr.x - pPrev.x, pCurr.y - pPrev.y);
    const dNext = Math.hypot(pNext.x - pCurr.x, pNext.y - pCurr.y);
    const r = Math.min(radius, dPrev / 2, dNext / 2);

    if (r <= 1) {
      d += ` L ${pCurr.x} ${pCurr.y}`;
      continue;
    }

    // Point before the corner
    const uPrevX = (pCurr.x - pPrev.x) / (dPrev || 1);
    const uPrevY = (pCurr.y - pPrev.y) / (dPrev || 1);
    const startX = pCurr.x - uPrevX * r;
    const startY = pCurr.y - uPrevY * r;

    // Point after the corner
    const uNextX = (pNext.x - pCurr.x) / (dNext || 1);
    const uNextY = (pNext.y - pCurr.y) / (dNext || 1);
    const endX = pCurr.x + uNextX * r;
    const endY = pCurr.y + uNextY * r;

    d += ` L ${startX} ${startY} Q ${pCurr.x} ${pCurr.y} ${endX} ${endY}`;
  }

  const pLast = points[points.length - 1]!;
  d += ` L ${pLast.x} ${pLast.y}`;

  return d;
}

export interface PipeElbow {
  readonly id: string;
  readonly x: number;
  readonly y: number;
  readonly radius: number;
  readonly entry: Point; // Start of the elbow fitting (collar 1)
  readonly exit: Point;  // End of the elbow fitting (collar 2)
  readonly inDir: Point;  // Incoming unit vector
  readonly outDir: Point; // Outgoing unit vector
}

export interface PipeRouteData {
  readonly pathData: string;
  readonly points: Point[];
  readonly elbows: PipeElbow[];
}

export function cleanCollinearPoints(points: Point[]): Point[] {
  if (points.length <= 2) return points;
  const result: Point[] = [points[0]!];

  for (let i = 1; i < points.length - 1; i++) {
    const prev = result[result.length - 1]!;
    const curr = points[i]!;
    const next = points[i + 1]!;

    if (Math.abs(curr.x - prev.x) < 0.5 && Math.abs(curr.y - prev.y) < 0.5) {
      continue;
    }

    const isHorizontal = Math.abs(prev.y - curr.y) < 0.5 && Math.abs(curr.y - next.y) < 0.5;
    const isVertical = Math.abs(prev.x - curr.x) < 0.5 && Math.abs(curr.x - next.x) < 0.5;

    if (!isHorizontal && !isVertical) {
      result.push(curr);
    }
  }

  const last = points[points.length - 1]!;
  const prevLast = result[result.length - 1]!;
  if (Math.abs(last.x - prevLast.x) > 0.5 || Math.abs(last.y - prevLast.y) > 0.5) {
    result.push(last);
  }

  return result;
}

export function extractPipeElbows(points: Point[], radius = 10): PipeElbow[] {
  const elbows: PipeElbow[] = [];
  if (points.length < 3) return elbows;

  for (let i = 1; i < points.length - 1; i++) {
    const pPrev = points[i - 1]!;
    const pCurr = points[i]!;
    const pNext = points[i + 1]!;

    const dx1 = pCurr.x - pPrev.x;
    const dy1 = pCurr.y - pPrev.y;
    const dx2 = pNext.x - pCurr.x;
    const dy2 = pNext.y - pCurr.y;

    const d1 = Math.hypot(dx1, dy1);
    const d2 = Math.hypot(dx2, dy2);
    if (d1 < 1 || d2 < 1) continue;

    const u1 = { x: dx1 / d1, y: dy1 / d1 };
    const u2 = { x: dx2 / d2, y: dy2 / d2 };

    const dot = u1.x * u2.x + u1.y * u2.y;
    if (Math.abs(dot) < 0.9) {
      const r = Math.min(radius, d1 / 2, d2 / 2);
      const entry = { x: pCurr.x - u1.x * r, y: pCurr.y - u1.y * r };
      const exit = { x: pCurr.x + u2.x * r, y: pCurr.y + u2.y * r };

      elbows.push({
        id: `elbow_${i}_${Math.round(pCurr.x)}_${Math.round(pCurr.y)}`,
        x: pCurr.x,
        y: pCurr.y,
        radius: r,
        entry,
        exit,
        inDir: u1,
        outDir: u2,
      });
    }
  }

  return elbows;
}

/**
 * Computes a clean industrial orthogonal route with waypoints and extracts 3D elbows.
 */
export function computePipeRoute(
  p1: Point,
  dir1: PortDirection,
  p2: Point,
  dir2: PortDirection,
  radius = 10,
  waypoints?: readonly Point[] | undefined
): PipeRouteData {
  const stub = 18;

  const getVector = (dir: PortDirection): Point => {
    switch (dir) {
      case 'up':
        return { x: 0, y: -1 };
      case 'down':
        return { x: 0, y: 1 };
      case 'left':
        return { x: -1, y: 0 };
      case 'right':
        return { x: 1, y: 0 };
    }
  };

  const v1 = getVector(dir1);
  const v2 = getVector(dir2);

  const s1: Point = { x: p1.x + v1.x * stub, y: p1.y + v1.y * stub };
  const s2: Point = { x: p2.x + v2.x * stub, y: p2.y + v2.y * stub };

  let rawPoints: Point[] = [];

  if (waypoints && waypoints.length > 0) {
    rawPoints = [p1, s1];
    let cur = s1;
    let curDir: 'h' | 'v' = (dir1 === 'left' || dir1 === 'right') ? 'h' : 'v';

    for (const wp of waypoints) {
      if (Math.abs(cur.x - wp.x) < 1 && Math.abs(cur.y - wp.y) < 1) continue;

      if (curDir === 'v') {
        if (Math.abs(cur.y - wp.y) >= 1) {
          rawPoints.push({ x: cur.x, y: wp.y });
        }
        if (Math.abs(cur.x - wp.x) >= 1) {
          rawPoints.push({ x: wp.x, y: wp.y });
          curDir = 'h';
        }
      } else {
        if (Math.abs(cur.x - wp.x) >= 1) {
          rawPoints.push({ x: wp.x, y: cur.y });
        }
        if (Math.abs(cur.y - wp.y) >= 1) {
          rawPoints.push({ x: wp.x, y: wp.y });
          curDir = 'v';
        }
      }
      cur = rawPoints[rawPoints.length - 1]!;
    }

    const enterDir: 'h' | 'v' = (dir2 === 'left' || dir2 === 'right') ? 'h' : 'v';
    if (enterDir === 'h') {
      if (Math.abs(cur.y - s2.y) >= 1) {
        rawPoints.push({ x: cur.x, y: s2.y });
      }
      rawPoints.push(s2);
    } else {
      if (Math.abs(cur.x - s2.x) >= 1) {
        rawPoints.push({ x: s2.x, y: cur.y });
      }
      rawPoints.push(s2);
    }
    rawPoints.push(p2);
  } else {
    const points: Point[] = [p1, s1];

    if (dir1 === 'right' && dir2 === 'left' && Math.abs(p1.y - p2.y) < 2 && p1.x < p2.x) {
      points.push(p2);
      rawPoints = points;
    } else if (dir1 === 'up' && dir2 === 'down' && Math.abs(p1.x - p2.x) < 2 && p1.y > p2.y) {
      points.push(p2);
      rawPoints = points;
    } else if (dir1 === 'up' && dir2 === 'up') {
      const yTop = Math.min(s1.y, s2.y) - 20;
      points.push({ x: s1.x, y: yTop });
      points.push({ x: s2.x, y: yTop });
      points.push(s2);
      points.push(p2);
      rawPoints = points;
    } else if (dir1 === 'right' && dir2 === 'left') {
      if (s1.x < s2.x) {
        const midX = (s1.x + s2.x) / 2;
        points.push({ x: midX, y: s1.y });
        points.push({ x: midX, y: s2.y });
      } else {
        const yMid = Math.max(s1.y, s2.y) + 30;
        points.push({ x: s1.x, y: yMid });
        points.push({ x: s2.x, y: yMid });
      }
      points.push(s2);
      points.push(p2);
      rawPoints = points;
    } else if (dir1 === 'up' && dir2 === 'left') {
      if (s1.y > s2.y && s1.x < s2.x) {
        points.push({ x: s1.x, y: s2.y });
      } else {
        const yMid = Math.min(s1.y, s2.y) - 16;
        points.push({ x: s1.x, y: yMid });
        points.push({ x: s2.x, y: yMid });
      }
      points.push(s2);
      points.push(p2);
      rawPoints = points;
    } else if (dir1 === 'right' && dir2 === 'up') {
      points.push({ x: s2.x, y: s1.y });
      points.push(s2);
      points.push(p2);
      rawPoints = points;
    } else {
      const midX = (s1.x + s2.x) / 2;
      points.push({ x: midX, y: s1.y });
      points.push({ x: midX, y: s2.y });
      points.push(s2);
      points.push(p2);
      rawPoints = points;
    }
  }

  const cleanPoints = cleanCollinearPoints(rawPoints);
  const elbows = extractPipeElbows(cleanPoints, radius);
  const pathData = roundOrthogonalCorners(cleanPoints, radius);

  return {
    pathData,
    points: cleanPoints,
    elbows,
  };
}

/**
 * Calculates a clean industrial orthogonal route between two ports, honoring their exit/entry normal vectors.
 */
export function computePipeOrthogonalPath(
  p1: Point,
  dir1: PortDirection,
  p2: Point,
  dir2: PortDirection,
  radius = 10,
  waypoints?: readonly Point[] | undefined
): string {
  return computePipeRoute(p1, dir1, p2, dir2, radius, waypoints).pathData;
}

/**
 * Calculates updated pipe waypoints when a segment or corner of a pipe is interactively dragged.
 */
export function calculateDraggedPipeWaypoints(
  p1: Point,
  dir1: PortDirection,
  p2: Point,
  dir2: PortDirection,
  initialRoutePoints: Point[],
  dragType: 'segment' | 'corner',
  segIdx: number | undefined,
  orientation: 'horizontal' | 'vertical' | undefined,
  cornerIdx: number | undefined,
  targetPoint: Point
): Point[] {
  const stub = 18;
  const getVec = (d: PortDirection): Point => {
    switch (d) {
      case 'up': return { x: 0, y: -1 };
      case 'down': return { x: 0, y: 1 };
      case 'left': return { x: -1, y: 0 };
      case 'right': return { x: 1, y: 0 };
    }
  };
  const v1 = getVec(dir1);
  const v2 = getVec(dir2);
  const s1: Point = { x: p1.x + v1.x * stub, y: p1.y + v1.y * stub };
  const s2: Point = { x: p2.x + v2.x * stub, y: p2.y + v2.y * stub };

  const pts = initialRoutePoints.map((p) => ({ ...p }));
  const M = pts.length;
  if (M < 2) return [];

  if (dragType === 'segment' && segIdx !== undefined && segIdx >= 0 && segIdx < M - 1) {
    const ptA = pts[segIdx]!;
    const ptB = pts[segIdx + 1]!;

    if (orientation === 'horizontal') {
      const targetY = targetPoint.y;
      const newPts: Point[] = [p1];

      if (segIdx === 0) {
        newPts.push(s1);
        newPts.push({ x: s1.x, y: targetY });
        newPts.push({ x: ptB.x, y: targetY });
        for (let i = 2; i < M; i++) newPts.push(pts[i]!);
      } else if (segIdx + 1 === M - 1) {
        for (let i = 1; i < segIdx; i++) newPts.push(pts[i]!);
        newPts.push({ x: ptA.x, y: targetY });
        newPts.push({ x: s2.x, y: targetY });
        newPts.push(s2);
        newPts.push(p2);
      } else {
        for (let i = 1; i < M - 1; i++) {
          if (i === segIdx || i === segIdx + 1) {
            newPts.push({ x: pts[i]!.x, y: targetY });
          } else {
            newPts.push(pts[i]!);
          }
        }
        newPts.push(p2);
      }

      const rawWps = newPts.slice(1, newPts.length - 1);
      return cleanCollinearPoints(rawWps);
    }

    if (orientation === 'vertical') {
      const targetX = targetPoint.x;
      const newPts: Point[] = [p1];

      if (segIdx === 0) {
        newPts.push(s1);
        newPts.push({ x: targetX, y: s1.y });
        newPts.push({ x: targetX, y: ptB.y });
        for (let i = 2; i < M; i++) newPts.push(pts[i]!);
      } else if (segIdx + 1 === M - 1) {
        for (let i = 1; i < segIdx; i++) newPts.push(pts[i]!);
        newPts.push({ x: targetX, y: ptA.y });
        newPts.push({ x: targetX, y: s2.y });
        newPts.push(s2);
        newPts.push(p2);
      } else {
        for (let i = 1; i < M - 1; i++) {
          if (i === segIdx || i === segIdx + 1) {
            newPts.push({ x: targetX, y: pts[i]!.y });
          } else {
            newPts.push(pts[i]!);
          }
        }
        newPts.push(p2);
      }

      const rawWps = newPts.slice(1, newPts.length - 1);
      return cleanCollinearPoints(rawWps);
    }
  }

  if (dragType === 'corner' && cornerIdx !== undefined && cornerIdx > 0 && cornerIdx < M - 1) {
    const targetX = targetPoint.x;
    const targetY = targetPoint.y;

    const newPts = pts.map((p) => ({ ...p }));
    const prevPt = pts[cornerIdx - 1]!;
    const nextPt = pts[cornerIdx + 1]!;

    newPts[cornerIdx] = { x: targetX, y: targetY };

    if (Math.abs(prevPt.y - pts[cornerIdx]!.y) < 1 && cornerIdx - 1 > 0) {
      newPts[cornerIdx - 1] = { x: prevPt.x, y: targetY };
    } else if (Math.abs(prevPt.x - pts[cornerIdx]!.x) < 1 && cornerIdx - 1 > 0) {
      newPts[cornerIdx - 1] = { x: targetX, y: prevPt.y };
    }

    if (Math.abs(nextPt.y - pts[cornerIdx]!.y) < 1 && cornerIdx + 1 < M - 1) {
      newPts[cornerIdx + 1] = { x: nextPt.x, y: targetY };
    } else if (Math.abs(nextPt.x - pts[cornerIdx]!.x) < 1 && cornerIdx + 1 < M - 1) {
      newPts[cornerIdx + 1] = { x: targetX, y: nextPt.y };
    }

    const rawWps = newPts.slice(1, newPts.length - 1);
    return cleanCollinearPoints(rawWps);
  }

  return cleanCollinearPoints(pts.slice(1, M - 1));
}
